/* eslint-disable no-promise-executor-return */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/self-closing-comp */
import React, { PureComponent, useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, InfoWindow } from '@react-google-maps/api';
import { Spin, Icon } from 'antd';
import mapStyle from './MapStyle';
import { ReactComponent as MarkerIcon } from './assets/marker.svg';
import pinIcons from './assets';
import { SpinIndicator } from '../PageLoader/PageLoader';
import useBusinessLocationAdvancedMarkers from './BusinessLocationAdvancedMarkers';
import {
  canUseAdvancedMapMarkers,
  getGoogleMapId,
  useGoogleMapsApi,
} from '../../utils/googleMaps';
import './BusinessLocation.scss';

const COLOR_BRANCH = '#5BC2E7';
const COLOR_CUSTOMER = '#F1C400';
const COLOR_SUPPLIER = '#CB2C30';
const COLOR_ALTERNATIVE_SITE = '#00685E';

const mapOptions = {
  zoomControl: true,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
};

const style = {
  legend: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '220px',
    height: '160px',
    padding: '20px 10px',
    margin: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, .8)',
  },
  legendItem: {
    marginLeft: '20px',
  },
};

const DEFAULT_CENTER_MELBOURNE = { lat: -37.8443907, lng: 145.0546349 };
const DEFAULT_CENTER_LONDON = { lat: 51.5074, lng: -0.1278 };

function getDefaultMapCenter() {
  const partner = (process.env.REACT_APP_PARTNER || '').toLowerCase().trim();
  if (partner === 'aviva') return DEFAULT_CENTER_LONDON;
  return DEFAULT_CENTER_MELBOURNE;
}

const MapComponent = ({ locations, geoCode, storeGeoCode }) => {
  const [positions, setPositions] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [map, setMap] = useState(null);
  const geocoderRef = useRef(null);
  const mapId = getGoogleMapId();

  const { isLoaded } = useGoogleMapsApi();

  const handleMarkerClick = useCallback((index) => {
    setSelectedMarker(index);
  }, []);

  useBusinessLocationAdvancedMarkers(map, positions, pinIcons, handleMarkerClick);

  const getGeoCodePromise = useCallback((address) => {
    return new Promise((resolve, reject) => {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      geocoderRef.current.geocode({ address }, (results, status) => {
        if (status === 'OK') {
          resolve(results);
        } else {
          reject(status);
        }
      });
    });
  }, []);

  const getGeoCode = useCallback(
    async (addressInfo) => {
      const res = await getGeoCodePromise(addressInfo.address);
      const lat = res[0].geometry.location.lat();
      const lng = res[0].geometry.location.lng();
      const result = { ...addressInfo, lat, lng };
      geoCode[addressInfo.address] = result;
      storeGeoCode(geoCode);
      return result;
    },
    [geoCode, storeGeoCode, getGeoCodePromise],
  );

  useEffect(() => {
    if (!isLoaded || !locations) return;

    const processCircuit = async (interval) => {
      const allLocations = {};
      const errors = [];
      let count = 0;
      const geoCodeInStore = [];

      Object.values(locations).forEach((addressList) => {
        addressList.forEach((addressInfo) => {
          if (addressInfo.address && !geoCode[addressInfo.address]) {
            const key = Math.floor(count / 5);
            if (allLocations[key] === undefined) {
              allLocations[key] = [];
            }
            allLocations[key].push(addressInfo);
            count += 1;
          } else if (geoCode[addressInfo.address]) {
            geoCodeInStore.push(geoCode[addressInfo.address]);
          }
        });
      });

      setPositions(geoCodeInStore);

      const timer = (i) => {
        return new Promise((resolve) =>
          setTimeout(() => {
            (async () => {
              // eslint-disable-next-line no-restricted-syntax
              for (const addressInfo of allLocations[i]) {
                try {
                  // eslint-disable-next-line no-await-in-loop
                  const result = await getGeoCode(addressInfo);
                  geoCodeInStore.push(result);
                  setPositions([...geoCodeInStore]);
                } catch (err) {
                  errors.push(err);
                }
              }
            })();
            resolve();
          }, interval),
        );
      };

      const countRequest = Object.keys(allLocations).length;

      const allocateGeoCodeLoop = async () => {
        for (let i = 0; i < countRequest; i++) {
          // eslint-disable-next-line no-await-in-loop
          await timer(i);
        }
      };

      await allocateGeoCodeLoop();
    };

    processCircuit(1000);
  }, [isLoaded, locations, geoCode, getGeoCode]);

  if (!canUseAdvancedMapMarkers()) {
    return (
      <div className="business-location-map-unconfigured">
        Location map requires <code>REACT_APP_GOOGLE_MAP_ID</code> (Google Cloud Map ID for Advanced Markers).
      </div>
    );
  }

  if (!isLoaded) {
    return <div style={{ height: '100%' }} />;
  }

  const center = positions.length > 0 ? positions[0] : getDefaultMapCenter();
  const selectedPosition = selectedMarker != null ? positions[selectedMarker] : null;

  return (
    <GoogleMap
      mapContainerStyle={{ height: '100%', width: '100%', padding: '20px' }}
      zoom={10}
      center={center}
      options={{
        mapId,
        ...mapOptions,
        styles: mapId ? undefined : mapStyle,
      }}
      onLoad={setMap}
      onUnmount={() => setMap(null)}
    >
      {selectedPosition && (
        <InfoWindow
          position={{ lat: selectedPosition.lat, lng: selectedPosition.lng }}
          onCloseClick={() => setSelectedMarker(null)}
          options={{ maxWidth: 280 }}
        >
          <div className="business-location-info-window__body">
            <button
              type="button"
              className="business-location-info-window__close"
              aria-label="Close"
              onClick={() => setSelectedMarker(null)}
            >
              &times;
            </button>
            <div className="business-location-info-window__title">{selectedPosition.name}</div>
            <div className="business-location-info-window__address">{selectedPosition.address}</div>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

/* eslint react/no-multi-comp: off */
export default class BusinessLocation extends PureComponent {
  state = {
    locations: null,
    loaded: false,
  };

  static getDerivedStateFromProps(nextProps) {
    const { branches, customerList, supplierList, alternativeSiteList } = nextProps;
    const lists = [branches, customerList, supplierList, alternativeSiteList];

    if (!lists.every(Array.isArray)) {
      return null;
    }

    const branchAddressList = branches.map((branch) => ({
      name: branch.name,
      address: branch.address,
      type: 'branch',
      color: COLOR_BRANCH,
    }));
    const customerAddressList = customerList.map((customer) => ({
      name: customer.name,
      address: customer.address,
      type: 'customer',
      color: COLOR_CUSTOMER,
    }));
    const supplierAddressList = supplierList.map((supplier) => ({
      name: supplier.name,
      address: supplier.address,
      type: 'supplier',
      color: COLOR_SUPPLIER,
    }));
    const alternativeSiteAddressList = alternativeSiteList.map((alternativeSite) => ({
      name: alternativeSite.name,
      address: alternativeSite.address,
      type: 'alternativeSite',
      color: COLOR_ALTERNATIVE_SITE,
    }));
    const locations = { branchAddressList, customerAddressList, supplierAddressList, alternativeSiteAddressList };
    return { locations, loaded: true };
  }

  render() {
    const { locations, loaded } = this.state;
    const { geoCode, storeGeoCode } = this.props;
    const hasAnyLocation =
      loaded &&
      locations &&
      Object.values(locations).some((addressList) => Array.isArray(addressList) && addressList.length > 0);

    return (
      <div className="widget">
        {loaded ? (
          <>
            <MapComponent locations={locations} geoCode={geoCode} storeGeoCode={storeGeoCode} />
            {!hasAnyLocation && (
              <div className="business-location-map-empty" role="status">
                <strong>No locations to display yet</strong>
                <p>Add branches, customers, suppliers, or recovery sites from Organisation to see them on the map.</p>
              </div>
            )}
            <ul style={style.legend}>
              <li style={{ color: COLOR_BRANCH }}>
                <Icon component={MarkerIcon} alt="branch" />
                <span style={style.legendItem}>Branches</span>
              </li>
              <li style={{ color: COLOR_CUSTOMER }}>
                <Icon component={MarkerIcon} alt="customer" />
                <span style={style.legendItem}>Customers</span>
              </li>
              <li style={{ color: COLOR_SUPPLIER }}>
                <Icon component={MarkerIcon} alt="supplier" />
                <span style={style.legendItem}>Suppliers</span>
              </li>
              <li style={{ color: COLOR_ALTERNATIVE_SITE }}>
                <Icon component={MarkerIcon} alt="recovery sites" />
                <span style={style.legendItem}>Short Term Recovery Sites</span>
              </li>
            </ul>
          </>
        ) : (
          <Spin className="loading" indicator={<SpinIndicator />} size="large" />
        )}
      </div>
    );
  }
}
